import {
  CSRF_HEADER,
  type DeleteFolderResult,
  type Folder,
  type FolderId,
  type FolderList,
  type FolderSection,
  MAX_FOLDER_DEPTH,
  type MovedItem,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { t } from "../i18n/i18n.ts";
import { useSession } from "../session.tsx";
import { wikiKeys } from "../wiki/queries.ts";
import { projectKeys } from "./queries.ts";

/**
 * The project's folders (spec `knowledge-folders`): every folder for members, only the folders
 * on the path of what a guest can read. Never a count of items.
 */
export const folderKeys = {
  list: (projectId: string) => ["projects", projectId, "folders"] as const,
};

export function useFolders(projectId: string) {
  return useQuery({
    queryKey: folderKeys.list(projectId),
    queryFn: async (): Promise<FolderList> =>
      ok(await api.api.projects[":projectId"].folders.$get({ param: { projectId } })),
  });
}

/** A refused folder operation in plain words (design §7); anything else as the server said. */
export function folderErrorText(error: Error): string {
  const code = error instanceof ApiError ? error.code : undefined;
  switch (code) {
    case "name_taken":
      return t("folders.error.nameTaken");
    case "folder_limit":
      return t("folders.error.limit");
    case "too_deep":
      return t("folders.error.tooDeep", { max: MAX_FOLDER_DEPTH });
    case "cycle":
      return t("folders.error.cycle");
    case "wrong_section":
      return t("folders.error.wrongSection");
    case "project_frozen":
      return t("folders.error.frozen");
    default:
      return error.message;
  }
}

export type FolderWrite =
  | { op: "create"; section: FolderSection; parentId: FolderId | null; name: string }
  | { op: "rename"; folderId: FolderId; name: string }
  | { op: "move"; folderId: FolderId; parentId: FolderId | null }
  | { op: "delete"; folderId: FolderId }
  | { op: "moveItem"; documentId: string; section: FolderSection; folderId: FolderId | null };

export type FolderWriteResult =
  | { op: "create" | "rename" | "move"; folder: Folder }
  | { op: "delete"; result: DeleteFolderResult }
  | { op: "moveItem"; moved: MovedItem; section: FolderSection };

/**
 * Every folder write through the `knowledge-folders` API, which authorises it; afterwards the
 * folders and the affected lists are read again (design §7).
 */
export function useFolderWrite(projectId: string) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };
  const projects = api.api.projects[":projectId"];
  return useMutation({
    mutationFn: async (write: FolderWrite): Promise<FolderWriteResult> => {
      switch (write.op) {
        case "create":
          return {
            op: "create",
            folder: await ok(
              await projects.folders.$post(
                {
                  param: { projectId },
                  json: { section: write.section, parentId: write.parentId, name: write.name },
                },
                headers,
              ),
            ),
          };
        case "rename":
          return {
            op: "rename",
            folder: await ok(
              await projects.folders[":folderId"].$patch(
                { param: { projectId, folderId: write.folderId }, json: { name: write.name } },
                headers,
              ),
            ),
          };
        case "move":
          return {
            op: "move",
            folder: await ok(
              await projects.folders[":folderId"].move.$post(
                {
                  param: { projectId, folderId: write.folderId },
                  json: { parentId: write.parentId },
                },
                headers,
              ),
            ),
          };
        case "delete":
          return {
            op: "delete",
            result: await ok(
              await projects.folders[":folderId"].$delete(
                { param: { projectId, folderId: write.folderId } },
                headers,
              ),
            ),
          };
        case "moveItem":
          return {
            op: "moveItem",
            section: write.section,
            moved: await ok(
              await projects.documents[":documentId"].folder.$put(
                {
                  param: { projectId, documentId: write.documentId },
                  json: { folderId: write.folderId },
                },
                headers,
              ),
            ),
          };
      }
    },
    onSettled: async (_data, _error, write) => {
      const lists =
        write.op === "moveItem"
          ? [write.section]
          : write.op === "delete"
            ? (["files", "wiki"] as const)
            : [];
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: folderKeys.list(projectId) }),
        ...lists.map((section) =>
          queryClient.invalidateQueries({
            queryKey:
              section === "wiki" ? wikiKeys.index(projectId) : projectKeys.documents(projectId),
          }),
        ),
      ]);
    },
  });
}
